/** Gap between tags (gap-1 = 4px) */
export const TAG_GAP = 4;

/** Room kept for the "+N more" badge when its own width is not measured. */
const OVERFLOW_BADGE_WIDTH = 60;

/**
 * How many tags of these widths fit on one line of `room` px, keeping
 * `badgeWidth` for the "+N more" badge whenever some are left over. Pass a
 * function to size the badge by how many it would hide ("+2 more" is narrower
 * than "+12 more"); an unknown or 0 width falls back to the estimate. With
 * any tags, never fewer than one, like the two-line layout: a first tag too wide for its room truncates, and the
 * line clips (DocumentTags `singleLine`) rather than spilling.
 */
export function fitTagsOnOneLine(
  widths: number[],
  room: number,
  badgeWidth: number | ((hidden: number) => number | undefined) = OVERFLOW_BADGE_WIDTH
): number {
  if (widths.length === 0) return 0;
  const badgeFor = (hidden: number) =>
    (typeof badgeWidth === "function" ? badgeWidth(hidden) : badgeWidth) || OVERFLOW_BADGE_WIDTH;
  const all = widths.reduce((sum, w, i) => sum + w + (i > 0 ? TAG_GAP : 0), 0);
  if (all <= room) return widths.length;
  let used = 0;
  let fit = 0;
  for (let i = 0; i < widths.length; i++) {
    used += (i > 0 ? TAG_GAP : 0) + widths[i];
    if (used > room) break;
    // Some are left over (they do not all fit), so the badge needs its room.
    // Keep looking past a miss: hiding fewer means a narrower badge, so a
    // longer run of tags can fit where a shorter one did not.
    if (used + TAG_GAP + badgeFor(widths.length - (i + 1)) <= room) fit = i + 1;
  }
  return Math.max(1, fit);
}

/**
 * The badge count measured on behalf of `hidden`: the widest count with as
 * many digits, capped at `maxHidden`. "+N more" grows with its digit count,
 * so one measured badge per digit length sizes every count at or above its
 * true width, without a hidden badge for each count.
 */
export function badgeMeasureCount(hidden: number, maxHidden: number): number {
  return Math.min(10 ** String(hidden).length - 1, maxHidden);
}

/** Every count `badgeMeasureCount` can name, for up to `maxHidden` hidden tags. */
export function badgeMeasureCounts(maxHidden: number): number[] {
  const counts: number[] = [];
  for (let digits = 1; maxHidden > 0 && 10 ** (digits - 1) <= maxHidden; digits++) {
    counts.push(Math.min(10 ** digits - 1, maxHidden));
  }
  return counts;
}
